import { Alert, Button, Modal, TimePicker } from 'antd';
import type { Moment } from 'moment';
import moment from 'moment';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { DateUtil } from '@src/packages/utils/dates';
import { ATTENDANCE_EVENT_TYPE } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import type { MarkAttendanceConfirmKind, MarkAttendanceJourneyDetails } from '../types';
import { clampMarkedAtToNow } from '../utils/clampMarkedAtToNow';
import {
  applyPickedTimeOnDate,
  getDisabledFutureHours,
  getDisabledFutureMinutes,
} from '../utils/getDisabledFutureTime';
import { isMarkOutsideScheduleTolerance } from '../utils/isMarkOutsideScheduleTolerance';
import { isShiftEndEarlierThanScheduled } from '../utils/isShiftEndEarlierThanScheduled';

import styles from '../MarkAttendanceDrawer.module.less';

type MarkAttendanceConfirmModalProps = {
  open: boolean;
  kind: MarkAttendanceConfirmKind | null;
  userDisplayName: string;
  markedAt: Date | null;
  journeyDetails: MarkAttendanceJourneyDetails | null;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onMarkedAtChange?: (next: Date) => void;
};

const TITLE_ID_BY_KIND: Record<MarkAttendanceConfirmKind, string> = {
  [ATTENDANCE_EVENT_TYPE.SHIFT_START]:
    'workJourney.realTime.markAttendance.confirmModal.title.shiftStart',
  [ATTENDANCE_EVENT_TYPE.SHIFT_END]:
    'workJourney.realTime.markAttendance.confirmModal.title.shiftEnd',
  [ATTENDANCE_EVENT_TYPE.BREAK_START]:
    'workJourney.realTime.markAttendance.confirmModal.title.breakStart',
  [ATTENDANCE_EVENT_TYPE.BREAK_END]:
    'workJourney.realTime.markAttendance.confirmModal.title.breakEnd',
};

const ACTION_ID_BY_KIND: Record<MarkAttendanceConfirmKind, string> = {
  [ATTENDANCE_EVENT_TYPE.SHIFT_START]:
    'workJourney.realTime.markAttendance.confirmModal.action.shiftStart',
  [ATTENDANCE_EVENT_TYPE.SHIFT_END]:
    'workJourney.realTime.markAttendance.confirmModal.action.shiftEnd',
  [ATTENDANCE_EVENT_TYPE.BREAK_START]:
    'workJourney.realTime.markAttendance.confirmModal.action.breakStart',
  [ATTENDANCE_EVENT_TYPE.BREAK_END]:
    'workJourney.realTime.markAttendance.confirmModal.action.breakEnd',
};

/** Confirm modal before calling `newAttendanceEvent`. */
const MarkAttendanceConfirmModal = ({
  open,
  kind,
  userDisplayName,
  markedAt,
  journeyDetails,
  submitting,
  onConfirm,
  onCancel,
  onMarkedAtChange,
}: MarkAttendanceConfirmModalProps) => {
  const intl = useIntl();

  const markingDateLabel =
    markedAt != null
      ? DateUtil.toDateString(markedAt, DateUtil.DEFAULT_DT_DATE_FORMAT)
      : '—';

  const timeValue = markedAt != null ? moment(markedAt) : null;

  const titleId = kind ? TITLE_ID_BY_KIND[kind] : TITLE_ID_BY_KIND[ATTENDANCE_EVENT_TYPE.SHIFT_START];
  const actionId = kind
    ? ACTION_ID_BY_KIND[kind]
    : ACTION_ID_BY_KIND[ATTENDANCE_EVENT_TYPE.SHIFT_START];
  const isEarlyShiftEnd = isShiftEndEarlierThanScheduled({
    kind,
    markedAt,
    journeyDetails,
  });
  const isOutsideScheduleTolerance =
    !isEarlyShiftEnd &&
    isMarkOutsideScheduleTolerance({
      kind,
      markedAt,
      journeyDetails,
    });

  const handleTimeChange = useCallback(
    (picked: Moment | null) => {
      if (!onMarkedAtChange || !markedAt) {
        return;
      }
      const applied = applyPickedTimeOnDate(markedAt, picked);
      if (!applied) {
        return;
      }
      onMarkedAtChange(clampMarkedAtToNow(applied));
    },
    [markedAt, onMarkedAtChange],
  );

  const canConfirm = markedAt != null && !submitting;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      destroyOnClose
      closable={!submitting}
      maskClosable={!submitting}
      className={styles.confirmModal}
    >
      <h3 className={styles.confirmModalTitle}>
        <IntlMessages id={titleId} />
      </h3>

      <div className={styles.confirmModalCard}>
        <div className={styles.confirmModalRow}>
          <span className={styles.confirmModalLabel}>
            <IntlMessages id="workJourney.realTime.markAttendance.field.user" />
          </span>
          <span className={styles.confirmModalValue}>{userDisplayName || '—'}</span>
        </div>
        <div className={styles.confirmModalRow}>
          <span className={styles.confirmModalLabel}>
            <IntlMessages id="workJourney.realTime.markAttendance.field.marking" />
          </span>
          <span className={styles.confirmModalValue}>{markingDateLabel}</span>
        </div>
        <div className={styles.confirmModalRow}>
          <span className={styles.confirmModalLabel}>
            <IntlMessages id="workJourney.realTime.markAttendance.field.time" />
          </span>
          <TimePicker
            className={styles.confirmModalTimePicker}
            format="HH:mm"
            allowClear={false}
            value={timeValue}
            disabled={submitting || !onMarkedAtChange}
            disabledHours={() => getDisabledFutureHours()}
            disabledMinutes={selectedHour => getDisabledFutureMinutes(selectedHour)}
            onChange={handleTimeChange}
            aria-label={intl.formatMessage({
              id: 'workJourney.realTime.markAttendance.field.time',
            })}
          />
        </div>
      </div>

      {isEarlyShiftEnd ? (
        <Alert
          className={styles.confirmModalWarning}
          type="warning"
          showIcon
          message={
            <IntlMessages id="workJourney.realTime.markAttendance.confirmModal.earlyShiftEndWarning" />
          }
        />
      ) : null}

      {isOutsideScheduleTolerance ? (
        <Alert
          className={styles.confirmModalWarning}
          type="warning"
          showIcon
          message={
            <IntlMessages id="workJourney.realTime.markAttendance.confirmModal.outsideScheduleWarning" />
          }
        />
      ) : null}

      <Button
        type="primary"
        block
        size="large"
        loading={submitting}
        disabled={!canConfirm}
        onClick={onConfirm}
        className={styles.confirmModalCta}
      >
        <IntlMessages id={actionId} />
      </Button>

      <p className={styles.confirmModalHint}>
        <IntlMessages id="workJourney.realTime.markAttendance.confirmModal.modifyHint" />
      </p>
    </Modal>
  );
};

export default MarkAttendanceConfirmModal;
